import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton,
  List, ListItem, ListItemText, Tooltip, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import notificationService from '../services/notificationService';

const SEVERITY_COLORS = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success',
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const NotificationDrawer = ({ open, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError('');
    try {
      const res = await notificationService.getRecent();
      setNotifications(res.data || []);
    } catch {
      setError('Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch {}
  };

  const handleDelete = async (id) => {
    try {
      await notificationService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleOpen = (actionUrl) => {
    if (actionUrl) {
      onClose();
      navigate(actionUrl);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 380 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6">Notifications</Typography>
        <Button size="small" onClick={handleMarkAllRead} sx={{ textTransform: 'none' }}>
          Mark all read
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && (
        <Box px={2} pt={2}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && notifications.length === 0 && (
        <Box px={2} pt={3}>
          <Typography variant="body2" color="text.secondary">No unread notifications.</Typography>
        </Box>
      )}

      <List disablePadding>
        {notifications.map((n, idx) => (
          <React.Fragment key={n.id}>
            {idx > 0 && <Divider />}
            <ListItem
              alignItems="flex-start"
              sx={{ bgcolor: n.read ? 'transparent' : 'action.hover', py: 1.5 }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {!n.read && (
                    <Tooltip title="Mark read">
                      <IconButton size="small" onClick={() => handleMarkRead(n.id)}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {n.actionUrl && (
                    <Tooltip title="Open">
                      <IconButton size="small" onClick={() => handleOpen(n.actionUrl)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(n.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 8 }}>
                    <Typography variant="body2" fontWeight={n.read ? 'normal' : 'bold'} noWrap>
                      {n.title}
                    </Typography>
                    <Chip
                      label={n.severity}
                      size="small"
                      color={SEVERITY_COLORS[n.severity] || 'default'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>
                }
                secondary={
                  <>
                    {n.message && (
                      <Typography variant="caption" display="block" color="text.secondary" noWrap>
                        {n.message}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {formatTime(n.createdAt)}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      {notifications.length > 0 && (
        <Box px={2} py={1.5} borderTop="1px solid" borderColor="divider">
          <Button fullWidth variant="text" size="small"
            onClick={() => { onClose(); navigate('/notifications'); }}
            sx={{ textTransform: 'none' }}>
            View all notifications
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default NotificationDrawer;
